import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useStateSelector, apiTokenSelector } from '../../data/global/dataSelectors';
import { useDispatch } from 'react-redux';
import { AccountManagementDataActions } from '../../data/global/actions/AccountManagementActions';
import {
  AddIcon,
  FormGroup,
  StepperField,
  LocalizeButton as Button,
  TextboxField,
  AddressField,
  Text,
  Loading,
  SelectDropdownField,
} from '@tradingblock/components';
import { FieldArray, Formik, Field } from 'formik';
import {
  AllAccountManagementBeneficiaryAccountTypes,
  AllAccountManagementBeneficiaryTypes,
  AllStates,
  BeneficiaryType,
  getGroupedCountryOptions,
  BeneficiaryAccountType,
  AccountManagementBeneficiaryUpdateModel,
  AddressModel,
  BeneficiariesRelationships,
} from '@tradingblock/types';
import _ from 'lodash';
import { DateField } from '../../components/form/DateField';
import { RemoveAllBeneficiaries } from './modals/RemoveAllBeneficiaries';
import { maskSSN } from '../admin/Applications/services/formatters';
import { DetailFields, DetailField } from '../../components/basic/DetailFields';
import { getInvalidError, getRequiredError } from './modals/Validation';
import { cleanDateModel } from '../../utilities/date';
import { useClearer } from '../../hooks/useClearer';
import { validateBeneficiaryPercentages, validateAddress } from '@tradingblock/components';
import Config from '../../config';

const BeneficiaryAddress = ({ address }: { address: AddressModel }) => (
  <span className="beneficiary-address mute">
    {address.address1 && (
      <>
        {address.address1}
        <br />
      </>
    )}
    {address.address2 && (
      <>
        {address.address2}
        <br />
      </>
    )}
    {address.city}
    {address.state && `, ${address.state}`} {address.postalCode}
    <br />
    {address.country}
  </span>
);

export const Beneficiaries: React.FunctionComponent<{}> = () => {
  const accountId = useStateSelector(s => s.account.accountId);
  const beneficiaries = useStateSelector(s => s.accountManagement.accountManagementDetails.details.beneficiaries);
  const beneArr = beneficiaries || [];
  const dispatch = useDispatch();
  const isUpdateLoading = useStateSelector(s => s.accountManagement.update.isLoading);
  const updateData = useStateSelector(s => s.accountManagement.update.data);
  const selectedLinkedAccountId = useStateSelector(
    s => s.account.selectedLinkedAccount && s.account.selectedLinkedAccount.accountId
  );
  const isLoaded = useStateSelector(s => s.accountManagement.accountDetails.isLoaded);
  const apiToken = useStateSelector(apiTokenSelector);

  const [addOrEditBeneficiary, setAddOrEditBeneficiary] = useState(false);
  const [showRemoveAll, setShowRemoveAll] = useState(false);
  const toggleShowRemoveAll = () => {
    showRemoveAll ? setShowRemoveAll(false) : setShowRemoveAll(true);
  };
  const clearer = useClearer();

  useEffect(() => {
    if (!isLoaded && accountId) {
      if (selectedLinkedAccountId) {
        dispatch(AccountManagementDataActions.requestAccountDetails({ accountId: selectedLinkedAccountId }));
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId: selectedLinkedAccountId }));
      } else {
        dispatch(AccountManagementDataActions.requestAccountDetails({ accountId }));
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
      }
    }
  }, [accountId, dispatch, updateData, isLoaded, selectedLinkedAccountId]);

  const initialBeneficiariesValues: AccountManagementBeneficiaryUpdateModel = {
    beneficiaries: beneArr,
    max: 10,
  };

  const onSubmit = useCallback(
    (values, { resetForm }) => {
      const cleanDateBeneficiaries = values.beneficiaries.map((b: any) => {
        if (b.dateOfBirth && b.beneficiaryType === BeneficiaryType.Individual) {
          b.dateOfBirth = cleanDateModel(b.dateOfBirth);
        }
        if (b.dateOfBirth && b.beneficiaryType === BeneficiaryType.Trust) {
          b.dateOfBirth = null;
        }
        return b;
      });

      let updateValues = {
        item: 'Beneficiaries',
        beneficiaries: cleanDateBeneficiaries,
      };

      dispatch(AccountManagementDataActions.requestMakeAccountUpdate({ accountId, request: updateValues }));

      if (!isUpdateLoading) {
        resetForm();
        setAddOrEditBeneficiary(false);
      }
    },
    [accountId, dispatch]
  );

  // TODO: Review localStorage usage
  const onValidate = (values: AccountManagementBeneficiaryUpdateModel) => {
    const errors: Record<string, any> = {};

    // Validate beneficiary percentages
    const percentageErrors = validateBeneficiaryPercentages(values.beneficiaries);
    Object.assign(errors, percentageErrors);

    // Validate individual beneficiary fields
    values.beneficiaries.forEach((b, i) => {
      // Validate address
      const addressErrors = validateAddress(
        {
          ...b.address,
          address1: b.address.address1 || '',
          address2: b.address.address2 || '',
          city: b.address.city || '',
          state: b.address.state || '',
          postalCode: b.address.postalCode || '',
          country: b.address.country || '',
        },
        Config.tradingApi,
        apiToken,
        `beneficiaries[${i}].address`
      );
      Object.assign(errors, addressErrors);

      if (b.beneficiaryType === BeneficiaryType.Individual) {
        if (!b.firstName) {
          errors[`beneficiaries[${i}].firstName`] = getRequiredError(b.firstName);
        }
        if (b.middleName && b.middleName.length > 20) {
          errors[`beneficiaries[${i}].middleName`] = getInvalidError(b.middleName);
        }
        if (!b.lastName) {
          errors[`beneficiaries[${i}].lastName`] = getRequiredError(b.lastName);
        }
        if (!b.ssnSecret) {
          errors[`beneficiaries[${i}].ssnSecret`] = getRequiredError(b.ssnSecret);
        }
        if (!b.relationship) {
          errors[`beneficiaries[${i}].relationship`] = getRequiredError(b.relationship);
        }
        if (!b.dateOfBirth.day) {
          errors[`beneficiaries[${i}].dateOfBirth`] = getRequiredError(b.dateOfBirth.day);
        }
        if (!b.dateOfBirth.month) {
          errors[`beneficiaries[${i}].dateOfBirth`] = getRequiredError(b.dateOfBirth.month);
        }
        if (!b.dateOfBirth.year) {
          errors[`beneficiaries[${i}].dateOfBirth`] = getRequiredError(b.dateOfBirth.year);
        }
      }

      if (b.beneficiaryType === BeneficiaryType.Trust) {
        if (!b.trustName) {
          errors[`beneficiaries[${i}].trustName`] = getRequiredError(b.trustName);
        }
        if (!b.einSecret) {
          errors[`beneficiaries[${i}].einSecret`] = getRequiredError(b.einSecret);
        }
      }

      if (!b.beneficiaryAccountType || !b.beneficiaryType) {
        errors[`beneficiaries[${i}].beneficiaryAccountType`] = getRequiredError(b.beneficiaryAccountType);
        errors[`beneficiaries[${i}].beneficiaryType`] = getRequiredError(b.beneficiaryType);
      }

      if (!b.percent) {
        errors[`beneficiaries[${i}].percent`] = getRequiredError(b.percent);
      }
      if (b.percent && !_.isInteger(b.percent)) {
        errors[`beneficiaries[${i}].percent`] = getInvalidError(b.percent);
      }
    });

    // Remove any undefined errors
    Object.keys(errors).forEach(key => (errors[key] === undefined ? delete errors[key] : {}));

    return errors;
  };

  const beneficiaryFields = useMemo(() => {
    let fieldList: DetailField[] = [];
    if (beneficiaries) {
      beneficiaries.map((b, i) => {
        fieldList = [
          ...fieldList,
          {
            Value: () => (
              <div
                key={i}
                className={`beneficiary-card ${
                  b.beneficiaryAccountType === BeneficiaryAccountType.Primary ? 'primary' : ''
                }`}
              >
                <div className="icon-corner" title={b.relationship}>
                  {b.relationship === 'Spouse' && <i className="fal fa-heart"></i>}
                  {b.relationship === 'Child' && <i className="fal fa-child"></i>}
                  {b.relationship === 'Parent' && <i className="fal fa-user-friends"></i>}
                  {b.relationship === 'Trust' && <i className="fal fa-handshake"></i>}
                  {b.relationship && !['Spouse', 'Child', 'Parent', 'Trust'].includes(b.relationship) && (
                    <i className="fal fa-user"></i>
                  )}
                </div>
                <div className="beneficiary-info">
                  {b.beneficiaryType === BeneficiaryType.Individual ? (
                    <div className={`individualBeneficiary-${i}`}>
                      <div className="d-flex align-items-center mb-3">
                        <span className="beneficiary-name">
                          {b.firstName} {b.lastName}
                        </span>
                        <span
                          className={`badge ${
                            b.beneficiaryAccountType === BeneficiaryAccountType.Primary
                              ? 'badge-primary'
                              : 'badge-contingent'
                          }`}
                        >
                          {b.beneficiaryAccountType}
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <div className="section-title mb-2">Personal Information</div>
                          <div className="mb-2">
                            <span>Relationship: </span>
                            <span className="mute">{b.relationship || 'Not found'}</span>
                          </div>

                          <div className="mb-2">
                            <span>Date of Birth: </span>
                            <span className="mute">
                              {b.dateOfBirth && (
                                <>
                                  {b.dateOfBirth.month}/{b.dateOfBirth.day}/{b.dateOfBirth.year}
                                </>
                              )}
                            </span>
                          </div>

                          <div className="mb-2">
                            <span>SSN/ITIN: </span>
                            <span className="mute">{b.ssnSecret ? maskSSN(b.ssnSecret) : 'Not found'}</span>
                          </div>

                          <div className="mb-4">
                            <span>Percent: </span>
                            <span className="mute">{b.percent || 'Not found'}</span>
                          </div>
                        </div>

                        <div className="col-12col-md-6">
                          <div className="address-section">
                            <div className="mb-2">Address</div>
                            <BeneficiaryAddress
                              address={{
                                address1: b.address.address1 || '',
                                address2: b.address.address2 || '',
                                city: b.address.city || '',
                                state: b.address.state || '',
                                postalCode: b.address.postalCode || '',
                                country: b.address.country || '',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`trustBeneficiary-${i}`}>
                      <div className="d-flex align-items-center mb-3">
                        <span className="beneficiary-name">{b.trustName || 'Not found'}</span>
                        <span
                          className={`badge ${
                            b.beneficiaryAccountType === BeneficiaryAccountType.Primary
                              ? 'badge-primary'
                              : 'badge-contingent'
                          }`}
                        >
                          {b.beneficiaryAccountType} - {b.beneficiaryType}
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <div className="section-title mb-2">Trust Information</div>
                          <div className="mb-2 mute">{b.trustName || 'Not found'}</div>
                          <div className="mb-4 mute">{b.einSecret || 'Not found'}</div>

                          <div className="mb-4">
                            <span>Percent: </span>
                            <span className="mute">{b.percent || 'Not found'}</span>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="address-section">
                            <div className="mb-2">Address</div>
                            <BeneficiaryAddress
                              address={{
                                address1: b.address.address1 || '',
                                address2: b.address.address2 || '',
                                city: b.address.city || '',
                                state: b.address.state || '',
                                postalCode: b.address.postalCode || '',
                                country: b.address.country || '',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ),
          },
        ];
      });
    }
    return fieldList;
  }, [beneficiaries]);

  return (
    <>
      {isUpdateLoading && (
        <div className="screen-body-section">
          <div className="beneficiaries-list">
            <h2 className="fields-title">Beneficiaries</h2>
          </div>
          <Loading />
        </div>
      )}
      {!isUpdateLoading && (
        <div className="screen-body-section">
          {beneficiaries && beneficiaries.length > 0 && !addOrEditBeneficiary && (
            <div className="beneficiaries-list">
              <h2 className="fields-title">
                <i className="fal fa-users dashboard-icon" /> Beneficiaries
              </h2>
              <DetailFields fieldList={beneficiaryFields} />

              <div className="block-footer-actions">
                <button
                  className="btn btn-danger mr-2"
                  onClick={e => {
                    e.preventDefault();
                    setShowRemoveAll(true);
                  }}
                >
                  Remove All Beneficiaries
                </button>
                <button
                  className="btn btn-primary block-action-button"
                  onClick={e => {
                    e.preventDefault();
                    setAddOrEditBeneficiary(true);
                  }}
                >
                  Edit/Add Beneficiaries
                </button>
              </div>
            </div>
          )}
          {(beneficiaries.length === 0 || addOrEditBeneficiary) && (
            <>
              {beneficiaries.length > 0 && (
                <p>
                  <button
                    className="btn btn-blend"
                    onClick={e => {
                      e.preventDefault();
                      setAddOrEditBeneficiary(false);
                    }}
                  >
                    <i className="fal fa-arrow-left" />
                    Go back
                  </button>
                </p>
              )}
              <p>
                <strong>Changes must be saved in order to take effect.</strong>
              </p>

              <div className="beneficiary-form">
                <Formik
                  initialValues={initialBeneficiariesValues}
                  onSubmit={onSubmit}
                  validate={onValidate}
                  validateOnChange={true}
                  validateOnBlur={true}
                >
                  {({ values, setValues, errors, touched, handleSubmit, isValid }) => (
                    <div className="form-section">
                      {Object.keys(errors).length > 0 && (
                        <div className="alert alert-danger">Please correct the errors below before submitting.</div>
                      )}
                      <FieldArray
                        name="beneficiaries"
                        render={arrayHelpers => (
                          <>
                            {values.beneficiaries.map((beneficiary, index) => (
                              <div
                                key={`beneficiary${index}`}
                                className="form-group-removable from-group-removable-titled"
                              >
                                <div className="row">
                                  <div className="col">
                                    <h2 className="fields-title">Retirement Account Beneficiary #{index + 1}</h2>
                                  </div>
                                  <div className="col-auto">
                                    <div className="remove">
                                      <Button
                                        type="remove"
                                        onClick={() => {
                                          if (values.beneficiaries.length === 1 && beneficiaries.length === 0) {
                                            setAddOrEditBeneficiary(false);
                                          }
                                          arrayHelpers.remove(index);
                                        }}
                                        tabIndex={-1}
                                      >
                                        <i className="fal fa-lg fa-times-circle" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>

                                <FormGroup>
                                  <Field
                                    component={StepperField}
                                    id={`beneficiaries[${index}].beneficiaryAccountType`}
                                    options={AllAccountManagementBeneficiaryAccountTypes}
                                  />
                                </FormGroup>
                                <FormGroup>
                                  <Field
                                    component={StepperField}
                                    id={`beneficiaries[${index}].beneficiaryType`}
                                    options={AllAccountManagementBeneficiaryTypes}
                                    disabledOptions={[BeneficiaryType.Trust]}
                                    onChanged={(beneficiaryType: BeneficiaryType) =>
                                      setValues({
                                        ...values,
                                        beneficiaries: _.map(values.beneficiaries, (b, j) => {
                                          if (index === j) {
                                            const isIndividual = beneficiaryType === BeneficiaryType.Individual;
                                            return {
                                              ...b,
                                              beneficiaryType,
                                              relationship: isIndividual ? b.relationship : undefined,
                                              firstName: isIndividual ? b.firstName : '',
                                              lastName: isIndividual ? b.lastName : '',
                                              dateOfBirth: isIndividual ? b.dateOfBirth : {},
                                              ssnSecret: isIndividual ? b.ssnSecret : '',
                                              trustName: isIndividual ? undefined : b.trustName,
                                              einSecret: isIndividual ? undefined : b.einSecret,
                                            };
                                          }
                                          return b;
                                        }),
                                      })
                                    }
                                  />
                                </FormGroup>
                                {beneficiary.beneficiaryType === BeneficiaryType.Individual && (
                                  <>
                                    <FormGroup>
                                      <Field
                                        component={SelectDropdownField}
                                        id={`beneficiaries[${index}].relationship`}
                                        isSearchable={false}
                                        options={BeneficiariesRelationships}
                                        defaultLabelId="relationship"
                                        optionsDefaultLabelId="beneficiaryRelationship"
                                      />
                                    </FormGroup>
                                    <FormGroup>
                                      <Field
                                        component={TextboxField}
                                        id={`beneficiaries[${index}].firstName`}
                                        defaultLabelId="firstName"
                                      />
                                    </FormGroup>
                                    <FormGroup>
                                      <Field
                                        component={TextboxField}
                                        id={`beneficiaries[${index}].middleName`}
                                        defaultLabelId="middleName"
                                      />
                                    </FormGroup>
                                    <FormGroup>
                                      <Field
                                        component={TextboxField}
                                        id={`beneficiaries[${index}].lastName`}
                                        defaultLabelId="lastName"
                                      />
                                    </FormGroup>
                                    <FormGroup>
                                      <Field
                                        component={SelectDropdownField}
                                        id={`beneficiaries[${index}].suffix`}
                                        defaultLabelId="suffix"
                                        optionsDefaultLabelId="suffix"
                                        options={[
                                          'None',
                                          'Jr',
                                          'Sr',
                                          'I',
                                          'II',
                                          'III',
                                          'IV',
                                          'V',
                                          'VI',
                                          'VII',
                                          'VIII',
                                          'IX',
                                        ]}
                                        isSearchable={false}
                                        isClearable
                                      />
                                    </FormGroup>
                                    <FormGroup>
                                      <Field
                                        component={DateField}
                                        id={`beneficiaries[${index}].dateOfBirth`}
                                        defaultLabelId="dateOfBirth"
                                      />
                                    </FormGroup>
                                    <FormGroup>
                                      <Field
                                        component={TextboxField}
                                        id={`beneficiaries[${index}].ssnSecret`}
                                        label={'SSN/ITIN'}
                                        type="password"
                                        allowUnmask={true}
                                      />
                                    </FormGroup>
                                  </>
                                )}
                                {beneficiary.beneficiaryType === BeneficiaryType.Trust && (
                                  <>
                                    <FormGroup>
                                      <Field component={TextboxField} id={`beneficiaries[${index}].trustName`} />
                                    </FormGroup>
                                    <FormGroup>
                                      <Field
                                        component={TextboxField}
                                        id={`beneficiaries[${index}].einSecret`}
                                        type="password"
                                        allowUnmask={true}
                                      />
                                    </FormGroup>
                                  </>
                                )}
                                <Field
                                  component={AddressField}
                                  id={`beneficiaries[${index}].address`}
                                  labelPrefix={'Beneficiary'}
                                  countryProps={{
                                    options: getGroupedCountryOptions(clearer),
                                    defaultLabelId: 'address.country',
                                    optionsDefaultLabelId: 'country',
                                    labelPrefix: 'Beneficiary',
                                  }}
                                  stateProps={{
                                    options: AllStates,
                                    defaultLabelId: 'address.state',
                                    optionsDefaultLabelId: 'state',
                                    labelPrefix: 'Beneficiary',
                                  }}
                                />
                                <FormGroup>
                                  <Field
                                    component={TextboxField}
                                    id={`beneficiaries[${index}].percent`}
                                    type="number"
                                    min="1"
                                    max="100"
                                    step="1"
                                  />
                                </FormGroup>
                                {errors.beneficiaries && (
                                  <p className="error">
                                    <strong>{errors.beneficiaries}</strong>{' '}
                                    <Text id={'beneficiaries.percent'} textKey={undefined} type={'error'} />
                                  </p>
                                )}
                              </div>
                            ))}
                            {values.max && values.beneficiaries.length < values.max && (
                              <p>
                                <button
                                  onClick={event => {
                                    event.currentTarget.blur();
                                    event.preventDefault();
                                    arrayHelpers.push({
                                      address: {
                                        address1: '',
                                        address2: '',
                                        city: '',
                                        postalCode: null,
                                        country: '',
                                        state: null,
                                      },
                                      beneficiaryAccountType: '',
                                      beneficiaryType: '',
                                      dateOfBirth: {
                                        day: undefined,
                                        month: undefined,
                                        year: undefined,
                                      },
                                      einSecret: '',
                                      firstName: '',
                                      middleName: '',
                                      lastName: '',
                                      suffix: '',
                                      percent: null,
                                      relationship: '',
                                      ssnSecret: '',
                                      trustName: '',
                                    });
                                    setAddOrEditBeneficiary(true);
                                  }}
                                  type="button"
                                >
                                  <AddIcon inverse={true} />
                                  <strong>Add Beneficiary</strong>
                                </button>{' '}
                                <Text
                                  id={`available-counts.${values.beneficiaries.length > 0 ? 'partial' : 'all'}`}
                                  data={{ max: values.max, left: values.max - values.beneficiaries.length }}
                                />
                              </p>
                            )}
                            {addOrEditBeneficiary && (
                              <div className="block-footer-actions">
                                {beneficiaries.length > 0 && (
                                  <>
                                    <button
                                      className="btn btn-blend mr-2"
                                      onClick={e => {
                                        e.preventDefault();
                                        setAddOrEditBeneficiary(false);
                                      }}
                                    >
                                      <i className="fal fa-arrow-left" />
                                      Go back
                                    </button>
                                  </>
                                )}

                                <button
                                  className="btn btn-primary block-action-button"
                                  type="submit"
                                  onClick={(values: any) => handleSubmit(values)}
                                >
                                  Save
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      />
                      <div className="form-actions">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={!isValid}
                          onClick={() => handleSubmit()}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  )}
                </Formik>
              </div>
            </>
          )}

          <RemoveAllBeneficiaries toggleModal={toggleShowRemoveAll} show={showRemoveAll} />
        </div>
      )}
    </>
  );
};
